/**
 * ContractsTableView - Tabela reutilizável de contratos
 * Segue mesmo padrão visual da aba Negócios (CustomerDealsTab):
 * - Tabela nativa HTML com scroll horizontal/vertical
 * - Header sticky, ordenação, filtros por status
 * - Colunas condicionais via props
 * 
 * @version 2.0.0
 * RELEASE: 2026-04-09 — Adição de colunas: Enviado, Assinado, Provider, Template unificado
 */
import React, { useState, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FileSignature,
  FileText,
  Eye,
  Briefcase,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Send,
  Link2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CONTRACT_STATUS_LABELS } from '@/types/contracts';
import type { ContractStatus } from '@/types/contracts';

// ============================================================================
// TIPOS
// ============================================================================

/** Contrato genérico aceito pela tabela (compatível com ambos os hooks) */
export interface ContractsTableContract {
  id: string;
  title: string;
  status: string;
  contract_number: string | number | null;
  created_at: string;
  /** Data de envio para assinatura */
  sent_at?: string | null;
  /** Data em que foi assinado */
  signed_at?: string | null;
  deal_id?: string;
  /** Título do negócio (usado quando showDealColumn=true) */
  deal_title?: string | null;
  /** Template usado */
  contract_templates?: {
    id?: string;
    name: string;
    category?: string | null;
  } | null;
  /** Signatários */
  contract_signatures?: Array<{
    id: string;
    status: string;
  }>;
  /** Versão do template */
  contract_template_versions?: {
    version_number: number;
  } | null;
  /** Tracking do provider de assinatura */
  contract_provider_tracking?: Array<{
    external_url?: string | null;
    external_document_id?: string | null;
    /** v1.3.0: nome amigável do provedor (vem da RPC via join em signature_providers) */
    provider_name?: string | null;
    provider_slug?: string | null;
  }>;
  /** Para contratos do deal que não possuem deal_title */
  used_template_version_id?: string | null;
}

/** Props do componente */
interface ContractsTableViewProps {
  /** Array de contratos */
  contracts: ContractsTableContract[] | undefined;
  /** Se está carregando */
  isLoading: boolean;
  /** Exibir coluna do negócio (true no modal Cliente) */
  showDealColumn?: boolean;
  /** Callback ao selecionar um contrato */
  onSelectContract: (id: string) => void;
  /** Ação no estado vazio (ex: botão "Gerar Contrato") */
  emptyAction?: React.ReactNode;
}

/** Campos de ordenação */
type SortField = 'title' | 'status' | 'created_at';

/** Direção da ordenação */
type SortDirection = 'asc' | 'desc';

/** Filtro por status */
type ContractStatusFilter = 'all' | ContractStatus;

// ============================================================================
// COMPONENTE: HEADER ORDENÁVEL
// ============================================================================

/**
 * Header de coluna com suporte a ordenação
 * Segue mesmo padrão do CustomerDealsTab
 */
function SortableHeader({
  field,
  label,
  currentField,
  direction,
  onSort,
  align = 'left',
}: {
  field: SortField;
  label: string;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const isActive = currentField === field;

  return (
    <TableHead
      className={cn(
        'text-xs font-medium py-2 cursor-pointer hover:bg-muted/30 transition-colors select-none',
        align === 'right' && 'text-right pr-4',
        align === 'center' && 'text-center'
      )}
      onClick={() => onSort(field)}
    >
      <div
        className={cn(
          'flex items-center gap-1',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center'
        )}
      >
        <span>{label}</span>
        {isActive ? (
          direction === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );
}

// ============================================================================
// UTILITÁRIO: COR DA BARRA LATERAL POR STATUS DO CONTRATO
// ============================================================================

/**
 * Retorna a classe de cor para a barra lateral baseado no status do contrato
 * Mesmo mapeamento usado na listagem CRM > Contratos Gerados
 */
function getContractStatusBarColor(status: string): string {
  switch (status) {
    case 'signed': return 'bg-green-500';
    case 'sent': return 'bg-amber-500';
    case 'generated': return 'bg-blue-500';
    case 'cancelled': return 'bg-destructive';
    case 'error':
    case 'expired': return 'bg-red-500';
    default: return 'bg-muted-foreground/40'; // draft e outros
  }
}

// ============================================================================
// COMPONENTE: LINHA DA TABELA
// ============================================================================

/**
 * Linha individual de contrato na tabela
 */
function ContractTableRow({
  contract,
  showDealColumn,
  onSelect,
}: {
  contract: ContractsTableContract;
  showDealColumn: boolean;
  onSelect: () => void;
}) {
  const statusInfo =
    CONTRACT_STATUS_LABELS[contract.status as ContractStatus] ||
    CONTRACT_STATUS_LABELS.draft;
  const signaturesCount = contract.contract_signatures?.length || 0;
  const signedCount =
    contract.contract_signatures?.filter((s) => s.status === 'signed').length || 0;

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50 group border-b border-border/40"
      onClick={onSelect}
    >
      {/* Barra lateral colorida por status */}
      <TableCell className="p-0 w-2">
        <div className={cn('w-1 h-full min-h-[40px] rounded-full', getContractStatusBarColor(contract.status))} />
      </TableCell>

      {/* Nº Contrato */}
      <TableCell className="px-2 py-1.5">
        {contract.contract_number ? (
          <span className="text-xs font-mono text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
            #{contract.contract_number}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </TableCell>

      {/* Título */}
      <TableCell className="font-medium max-w-[140px] px-2 py-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="truncate block text-sm">{contract.title}</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[300px]">
            <p className="text-xs">{contract.title}</p>
          </TooltipContent>
        </Tooltip>
      </TableCell>

      {/* Status */}
      <TableCell className="px-2 py-1.5">
        {/* v2.1.0 — bolinha colorida + rótulo em cor padrão (substitui o badge) */}
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'h-2 w-2 shrink-0 rounded-full',
              getContractStatusBarColor(contract.status)
            )}
          />
          <span className="text-xs text-foreground">{statusInfo.label}</span>
        </div>
      </TableCell>

      {/* Negócio (condicional) */}
      {showDealColumn && (
        <TableCell className="px-2 py-1.5 max-w-[120px]">
          {contract.deal_title ? (
            <div className="flex items-center gap-1">
              <Briefcase className="h-3 w-3 shrink-0 text-muted-foreground" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-muted-foreground truncate">
                    {contract.deal_title}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-xs">{contract.deal_title}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          )}
        </TableCell>
      )}

      {/* Assinaturas */}
      <TableCell className="px-2 py-1.5 text-center">
        {signaturesCount > 0 ? (
          <span className="text-xs text-muted-foreground flex items-center gap-1 justify-center">
            <FileSignature className="h-3 w-3" />
            {signedCount}/{signaturesCount}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </TableCell>

      {/* Provider — v1.3.0: exibe o NOME do provedor (não o UUID) */}
      <TableCell className="px-2 py-1.5">
        {contract.contract_provider_tracking && contract.contract_provider_tracking.length > 0 ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Link2 className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {contract.contract_provider_tracking[0]?.provider_name || 'Vinculado'}
                </span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">
                {contract.contract_provider_tracking[0]?.provider_name || 'Provider vinculado'}
                {contract.contract_provider_tracking[0]?.external_document_id
                  ? ` · ID: ${contract.contract_provider_tracking[0].external_document_id}`
                  : ''}
              </p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-xs text-muted-foreground">–</span>
        )}
      </TableCell>


      {/* Template (nome + versão unificados) */}
      <TableCell className="px-2 py-1.5 max-w-[120px]">
        {contract.contract_templates?.name ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground truncate">
                  {contract.contract_templates.name}
                </span>
                {contract.contract_template_versions?.version_number != null && (
                  <span className="font-mono text-[10px] text-muted-foreground/70">
                    v1.{contract.contract_template_versions.version_number - 1}
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">{contract.contract_templates.name}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-xs text-muted-foreground">–</span>
        )}
      </TableCell>

      {/* Criado */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {format(new Date(contract.created_at), 'dd/MM/yy', { locale: ptBR })}
      </TableCell>

      {/* Enviado */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {contract.sent_at ? (
          <span className="flex items-center gap-1">
            <Send className="h-3 w-3 text-amber-500" />
            {format(new Date(contract.sent_at), 'dd/MM/yy', { locale: ptBR })}
          </span>
        ) : (
          <span>–</span>
        )}
      </TableCell>

      {/* Assinado */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {contract.signed_at ? (
          <span className="flex items-center gap-1">
            <FileSignature className="h-3 w-3 text-emerald-500" />
            {format(new Date(contract.signed_at), 'dd/MM/yy', { locale: ptBR })}
          </span>
        ) : (
          <span>–</span>
        )}
      </TableCell>

      {/* Ação: Visualizar */}
      <TableCell className="w-6 px-1 py-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                onSelect();
              }}
            >
              <Eye className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p className="text-xs">Ver detalhes</p>
          </TooltipContent>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// STATUS DISPONÍVEIS PARA FILTRO
// ============================================================================

/** Status exibidos nos filtros */
const FILTER_STATUSES: { value: ContractStatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'generated', label: 'PDF Gerado' },
  { value: 'sent', label: 'Enviado' },
  { value: 'signed', label: 'Assinado' },
  { value: 'cancelled', label: 'Cancelado' },
];

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

/**
 * Tabela reutilizável de contratos
 * Segue exatamente o padrão visual do CustomerDealsTab
 */
export function ContractsTableView({
  contracts,
  isLoading,
  showDealColumn = false,
  onSelectContract,
  emptyAction,
}: ContractsTableViewProps) {
  // ========== ESTADOS ==========
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [statusFilter, setStatusFilter] = useState<ContractStatusFilter>('all');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ========== PROCESSAMENTO DE DADOS ==========

  /** Filtra contratos por status */
  const filteredContracts = useMemo(() => {
    if (!contracts) return [];
    if (statusFilter === 'all') return contracts;
    return contracts.filter((c) => c.status === statusFilter);
  }, [contracts, statusFilter]);

  /** Ordena contratos */
  const sortedContracts = useMemo(() => {
    return [...filteredContracts].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'created_at':
          comparison =
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredContracts, sortField, sortDirection]);

  // ========== HANDLERS ==========

  /** Alterna ordenação */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // ========== CONTADORES ==========
  const totalContracts = contracts?.length || 0;
  const statusCounts = useMemo(() => {
    if (!contracts) return {} as Record<string, number>;
    const counts: Record<string, number> = {};
    contracts.forEach((c) => {
      counts[c.status] = (counts[c.status] || 0) + 1;
    });
    return counts;
  }, [contracts]);

  // ========== SKELETON LOADING ==========
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-6 w-14" />
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-border/50 overflow-hidden">
          <div className="overflow-auto">
            <table className="w-max min-w-[800px] caption-bottom text-sm table-fixed">
              <TableHeader>
                <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <TableHead key={i} className="py-2">
                      <Skeleton className="h-3 w-10" />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[1, 2, 3].map((i) => (
                  <TableRow key={i} className="border-b border-border/40">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                      <TableCell key={j} className="py-2">
                        <Skeleton className="h-4 w-14" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ========== ESTADO VAZIO ==========
  if (!contracts || contracts.length === 0) {
    return (
      <div className="rounded-lg border border-border/50 bg-muted/20 p-8 text-center">
        <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
          <FileText className="h-6 w-6 text-muted-foreground" />
        </div>
        <h4 className="text-sm font-medium mb-1">Nenhum contrato encontrado</h4>
        <p className="text-xs text-muted-foreground mb-3">
          Contratos são gerados dentro de cada negócio
        </p>
        {emptyAction}
      </div>
    );
  }

  // ========== MIN-WIDTH DINÂMICO ==========
  /** Min-width ajustado para novas colunas */
  const minWidth = showDealColumn ? '1150px' : '1030px';

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 w-full max-w-full overflow-hidden">
      {/* ========== HEADER FIXO: Resumo + Filtros ========== */}
      <div className="flex-shrink-0 space-y-2 pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Resumo */}
          <div className="flex items-center gap-4 text-xs">
            <span className="text-muted-foreground">
              Total: <strong className="text-foreground">{totalContracts}</strong>
            </span>
            {statusCounts.signed && (
              <span className="text-emerald-600">
                Assinados: <strong>{statusCounts.signed}</strong>
              </span>
            )}
            {statusCounts.sent && (
              <span className="text-amber-600">
                Enviados: <strong>{statusCounts.sent}</strong>
              </span>
            )}
            {statusCounts.draft && (
              <span className="text-muted-foreground">
                Rascunhos: <strong>{statusCounts.draft}</strong>
              </span>
            )}
          </div>

          {/* Filtros por status */}
          <div className="flex items-center gap-1">
            {FILTER_STATUSES.map((fs) => {
              const statusLabel =
                fs.value !== 'all'
                  ? CONTRACT_STATUS_LABELS[fs.value as ContractStatus]
                  : null;
              return (
                <Button
                  key={fs.value}
                  variant={statusFilter === fs.value ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={() => setStatusFilter(fs.value)}
                >
                  {statusLabel && (
                    <span
                      className={cn(
                        'w-1.5 h-1.5 rounded-full mr-1',
                        fs.value === 'signed' && 'bg-emerald-500',
                        fs.value === 'sent' && 'bg-amber-500',
                        fs.value === 'draft' && 'bg-muted-foreground',
                        fs.value === 'generated' && 'bg-blue-500',
                        fs.value === 'cancelled' && 'bg-rose-500'
                      )}
                    />
                  )}
                  {fs.label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Indicador de resultados filtrados */}
        {statusFilter !== 'all' && (
          <p className="text-xs text-muted-foreground">
            Exibindo {sortedContracts.length} de {totalContracts} contratos
          </p>
        )}
      </div>

      {/* ========== TABELA COM SCROLL ========== */}
      <div className="flex-1 min-h-0 w-full max-w-full min-w-0 rounded-lg border border-border/50 overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="h-full w-full max-w-full overflow-auto"
        >
          <table
            className={cn(
              'w-max caption-bottom text-sm table-fixed',
              `min-w-[${minWidth}]`
            )}
            style={{ minWidth }}
          >
            {/* Larguras de colunas */}
            {/* Larguras de colunas ajustadas para layout v2.0 */}
            <colgroup>
              <col className="w-[8px]" />    {/* Barra de status */}
              <col className="w-[75px]" />   {/* Nº Contrato */}
              <col className="w-[130px]" />  {/* Título */}
              <col className="w-[85px]" />   {/* Status */}
              {showDealColumn && <col className="w-[110px]" />} {/* Negócio */}
              <col className="w-[70px]" />   {/* Assinaturas */}
              <col className="w-[80px]" />   {/* Provider */}
              <col className="w-[120px]" />  {/* Template */}
              <col className="w-[70px]" />   {/* Criado */}
              <col className="w-[80px]" />   {/* Enviado */}
              <col className="w-[80px]" />   {/* Assinado */}
              <col className="w-[36px]" />   {/* Ação */}
            </colgroup>

            {/* Header sticky */}
            <TableHeader className="sticky top-0 bg-background z-[1]">
              <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
                <TableHead className="w-2 p-0" /> {/* Barra de status */}
                <TableHead className="text-xs font-medium px-2 py-1.5">
                  Nº
                </TableHead>
                <SortableHeader
                  field="title"
                  label="Título"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="status"
                  label="Status"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                {showDealColumn && (
                  <TableHead className="text-xs font-medium px-2 py-1.5">
                    Negócio
                  </TableHead>
                )}
                <TableHead className="text-xs font-medium text-center px-2 py-1.5">
                  Assinaturas
                </TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">
                  Provider
                </TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">
                  Template
                </TableHead>
                <SortableHeader
                  field="created_at"
                  label="Criado"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <TableHead className="text-xs font-medium px-2 py-1.5">
                  Enviado
                </TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">
                  Assinado
                </TableHead>
                <TableHead className="w-6 px-1 py-1.5" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {sortedContracts.map((contract) => (
                <ContractTableRow
                  key={contract.id}
                  contract={contract}
                  showDealColumn={showDealColumn}
                  onSelect={() => onSelectContract(contract.id)}
                />
              ))}
            </TableBody>
          </table>
        </div>
      </div>
    </div>
  );
}
